import { useMemo } from 'react';
import { MolocoAdLPProduct, MolocoAdLPBaseData } from '@models/moloco';
import {
  VirtualizedItem,
  ProductType,
} from '@components/common/LpFilterProductList/types';
import {
  getLpProductListColumnPosition,
  type LpProductListCols,
} from '@wconcept/wconcept-common/utils/lpProductListCols';

interface UseMergedProductsWithAdsParams {
  products: ProductType[];
  molocoProducts?: MolocoAdLPProduct[];
  molocoAdLpBaseData?: MolocoAdLPBaseData;
  isMobile: boolean;
  mobileColumnCount?: LpProductListCols;
}

export function useMergedProductsWithAds({
  products,
  molocoProducts,
  molocoAdLpBaseData,
  isMobile,
  mobileColumnCount = 2,
}: UseMergedProductsWithAdsParams): VirtualizedItem[] {
  return useMemo(() => {
    const webPosition = molocoAdLpBaseData?.webPosition;
    const productItems = products.map((product, index) => ({
      type: 'product' as const,
      product,
      originalIndex: index,
      productIndex: index,
    }));

    if (
      !isMobile ||
      !molocoProducts?.length ||
      !webPosition ||
      products.length < 6
    ) {
      return productItems;
    }

    const offset = getLpProductListColumnPosition(
      webPosition.offset,
      webPosition.threeColumnOffset,
      mobileColumnCount
    );
    const step = getLpProductListColumnPosition(
      webPosition.step,
      webPosition.threeColumnStep,
      mobileColumnCount
    );

    if (offset === undefined || step === undefined) {
      return productItems;
    }

    const result: VirtualizedItem[] = [];
    let molocoIndex = 0;

    for (let i = 0; i < products.length; i++) {
      const nextAdPosition = offset + molocoIndex * (step + 1);

      if (
        molocoIndex < molocoProducts.length &&
        result.length === nextAdPosition
      ) {
        const adProduct = molocoProducts[molocoIndex];
        result.push({
          type: 'productAd',
          molocoProduct: adProduct,
          originalIndex: i + molocoIndex, // 상품 + 광고상품 인덱스
          productIndex: i, // 상품 인덱스
        });
        molocoIndex += 1;
      }

      result.push({
        type: 'product',
        product: products[i],
        originalIndex: i + molocoIndex,
        productIndex: i,
      });
    }

    return result;
  }, [
    products,
    molocoProducts,
    molocoAdLpBaseData,
    isMobile,
    mobileColumnCount,
  ]);
}
